// pages/Users/EditUser.js - Complete with Staff and Customer fields only

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  FaArrowLeft, FaSave, FaStore, FaBuilding, FaMapMarkerAlt,
  FaUserTag, FaLock, FaSpinner, FaAddressCard, FaCheckCircle,
  FaPhone, FaEnvelope, FaIdCard, FaUserEdit, FaTrash
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import axiosInstance from '../../utils/axiosConfig';
import './users.css';

const EditUser = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id, shopId, userId } = useParams();

  // Get the actual user ID from params (could be 'id' or 'userId')
  const actualUserId = id || userId;

  // Get shop ID from URL params or path
  const pathMatch = location.pathname.match(/\/shop\/(\d+)\/edit-user/);
  const shopIdFromPath = pathMatch ? pathMatch[1] : shopId;

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState(null);
  const [shops, setShops] = useState([]);
  const [companyAddresses, setCompanyAddresses] = useState([]);
  const [loadingShops, setLoadingShops] = useState(true);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // City selection states
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);

  // ✅ Address form state
  const [userAddress, setUserAddress] = useState({
    house_no: '',
    street: '',
    area: '',
    postcode: ''
  });

  const [formData, setFormData] = useState({
    id: '',
    first_Name: '',
    last_Name: '',
    email: '',
    phone_Number: '',
    type: 'STAFF',
    company_name: '',
    company_size: '',
    Vat_number: '',
    is_active: true,
    shop_id: '',
    company_address: '',
    selected_city: ''
  });

  const [selectedShop, setSelectedShop] = useState('');
  const [selectedCompanyAddress, setSelectedCompanyAddress] = useState('');
  const [newPassword, setNewPassword] = useState('');

  // User type options - Only Staff and Customer (Admin hidden)
  const userTypes = [
    { value: 'STAFF', label: 'Staff' },
    { value: 'CUSTOMER', label: 'Customer' }
  ];

  // Company size options
  const companySizes = [
    { value: '', label: 'Select company size' },
    { value: '1-10', label: '1-10 employees' },
    { value: '11-50', label: '11-50 employees' },
    { value: '51-200', label: '51-200 employees' },
    { value: '201-500', label: '201-500 employees' },
    { value: '500+', label: '500+ employees' }
  ];

  // Fetch user data, shops, and company addresses
  useEffect(() => {
    if (actualUserId) {
      fetchUserData();
    } else {
      setError('User ID not found');
      setFetching(false);
    }
    fetchShops();
  }, [actualUserId]);

  // Fetch company addresses and cities when shop changes
  useEffect(() => {
    if (selectedShop && selectedShop !== '') {
      fetchCompanyAddresses(selectedShop);
      fetchShopCities(selectedShop);
    }
  }, [selectedShop]);

  const fetchUserData = async () => {
    try {
      setFetching(true);
      setError(null);
      console.log('Fetching user with ID:', actualUserId);

      const response = await axiosInstance.get(`/user/users/${actualUserId}/`);
      console.log('User data received:', response.data);

      const user = response.data;

      setFormData({
        id: user.id || '',
        first_Name: user.first_Name || '',
        last_Name: user.last_Name || '',
        email: user.email || '',
        phone_Number: user.phone_Number || '',
        type: user.type || 'STAFF',
        company_name: user.company_name || '',
        company_size: user.company_size || '',
        Vat_number: user.Vat_number || '',
        is_active: user.is_active !== undefined ? user.is_active : true,
        shop_id: user.shop || '',
        company_address: user.company_address || '',
        selected_city: user.selected_city || ''
      });

      // ✅ FIX: Load address data from user's address
      if (user.address && typeof user.address === 'object') {
        setUserAddress({
          house_no: user.address.house_Number || '',
          street: user.address.street_Number || '',
          area: user.address.area || '',
          postcode: user.address.postal_code || ''
        });
      } else {
        setUserAddress({
          house_no: '',
          street: '',
          area: '',
          postcode: ''
        });
      }

      setSelectedShop(user.shop ? String(user.shop) : '');
      setSelectedCompanyAddress(user.company_address ? String(user.company_address) : '');

      if (user.shop) {
        await fetchShopCities(String(user.shop));
      }

    } catch (err) {
      console.error('Error fetching user:', err);
      console.error('Error details:', err.response?.data);

      if (err.response?.status === 404) {
        setError('User not found');
      } else if (err.response?.status === 401) {
        setError('Session expired. Please login again.');
        setTimeout(() => navigate('/signin'), 2000);
      } else {
        setError('Failed to load user data. Please try again.');
      }
    } finally {
      setFetching(false);
    }
  };

  const fetchShops = async () => {
    try {
      setLoadingShops(true);
      const response = await axiosInstance.get('/user/shops/');
      setShops(response.data);
    } catch (err) {
      console.error('Error fetching shops:', err);
    } finally {
      setLoadingShops(false);
    }
  };

  const fetchCompanyAddresses = async (shopId) => {
    if (!shopId) return;

    try {
      setLoadingAddresses(true);
      const response = await axiosInstance.get(`/user/company-addresses/?shop_id=${shopId}`);
      setCompanyAddresses(response.data);
    } catch (err) {
      console.error('Error fetching company addresses:', err);
      setCompanyAddresses([]);
    } finally {
      setLoadingAddresses(false);
    }
  };

  const fetchShopCities = async (shopId) => {
    if (!shopId) {
      setCities([]);
      setFormData(prev => ({ ...prev, selected_city: '' }));
      return;
    }

    try {
      setLoadingCities(true);
      const response = await axiosInstance.get(`/user/shop-cities/?shop_id=${shopId}`);
      console.log('Shop cities response:', response.data);

      if (response.data && response.data.cities) {
        const citiesData = Array.isArray(response.data.cities) ? response.data.cities : [];
        setCities(citiesData);
      } else {
        setCities([]);
      }
    } catch (err) {
      console.error('Error fetching shop cities:', err);
      setCities([]);
    } finally {
      setLoadingCities(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // ✅ Handle user address form changes
  const handleUserAddressChange = (e) => {
    const { name, value } = e.target;
    setUserAddress(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleShopChange = (e) => {
    const newShopId = e.target.value;
    setSelectedShop(newShopId);
    setSelectedCompanyAddress('');
    setFormData(prev => ({
      ...prev,
      shop_id: newShopId,
      company_address: '',
      selected_city: ''
    }));
  };

  // ✅ Build the full address string from the address fields
  const buildFullAddress = () => {
    const parts = [
      userAddress.house_no.trim(),
      userAddress.street.trim(),
      userAddress.area.trim(),
      userAddress.postcode.trim()
    ].filter(Boolean);
    return parts.join(', ');
  };

  const validateForm = () => {
    if (!formData.first_Name || formData.first_Name.trim() === '') {
      Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: 'First name is required',
        timer: 2000,
        showConfirmButton: true
      });
      return false;
    }

    if (!formData.last_Name || formData.last_Name.trim() === '') {
      Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: 'Last name is required',
        timer: 2000,
        showConfirmButton: true
      });
      return false;
    }

    if (!formData.email || formData.email.trim() === '') {
      Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: 'Email is required',
        timer: 2000,
        showConfirmButton: true
      });
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: 'Please enter a valid email address',
        timer: 2000,
        showConfirmButton: true
      });
      return false;
    }

    if (!selectedShop) {
      Swal.fire({
        icon: 'warning',
        title: 'Shop Required',
        text: 'Please select a shop for this user',
        timer: 2000,
        showConfirmButton: true
      });
      return false;
    }

    if (formData.type === 'STAFF') {
      if (selectedShop && cities.length > 0 && !formData.selected_city) {
        Swal.fire({
          icon: 'warning',
          title: 'City Required',
          text: 'Please select a city for staff user',
          timer: 2000,
          showConfirmButton: true
        });
        return false;
      }
    }

    if (newPassword && newPassword.length < 8) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: 'Password must be at least 8 characters long',
        timer: 2000,
        showConfirmButton: true
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    // ✅ Build address object for proper saving
    const addressObject = {
      city: formData.selected_city || '',
      area: userAddress.area || '',
      street_Number: userAddress.street || '',
      house_Number: userAddress.house_no || '',
      postal_code: userAddress.postcode || ''
    };

    const userData = {
      first_Name: formData.first_Name.trim(),
      last_Name: formData.last_Name.trim(),
      email: formData.email.trim(),
      phone_Number: formData.phone_Number || '',
      type: formData.type,
      shop_id: selectedShop || null,
      company_name: formData.company_name || '',
      company_size: formData.company_size || '',
      Vat_number: formData.Vat_number || '',
      company_address: selectedCompanyAddress || null,
      is_active: formData.is_active,
      selected_city: formData.type === 'ADMIN' ? '' : formData.selected_city,
      address: addressObject,
    };

    if (newPassword && newPassword.length >= 8) {
      userData.password = newPassword;
    }

    try {
      const response = await axiosInstance.put(`/user/users/${actualUserId}/`, userData);

      if (response.status === 200) {
        await Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: `${formData.type} ${formData.first_Name} ${formData.last_Name} updated successfully!`,
          timer: 2500,
          showConfirmButton: true
        });

        if (shopIdFromPath || selectedShop) {
          const redirectShop = shopIdFromPath || selectedShop;
          navigate(`/shop/${redirectShop}/users`);
        } else {
          navigate('/users');
        }
      }
    } catch (err) {
      console.error('Error updating user:', err);
      const errorMsg = err.response?.data?.error || err.response?.data?.message || 'Failed to update user';
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMsg,
        timer: 3000,
        showConfirmButton: true
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    try {
      await axiosInstance.delete(`/user/users/${actualUserId}/`);
      await Swal.fire({
        icon: 'success',
        title: 'Deleted!',
        text: `${formData.type} deleted successfully!`,
        timer: 2000,
        showConfirmButton: true
      });
      navigate('/users');
    } catch (err) {
      console.error('Error deleting user:', err);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to delete user',
        timer: 3000,
        showConfirmButton: true
      });
    } finally {
      setShowDeleteModal(false);
    }
  };

  // Get current shop name for display
  const currentShop = shops.find(s => s.shop_id === parseInt(selectedShop));

  // True if the current type is a legacy type not in the dropdown
  const isLegacyType = formData.type && !userTypes.some(t => t.value === formData.type);

  // Show error state
  if (error) {
    return (
      <div className="error-container">
        <div className="error-content">
          <h2>Error</h2>
          <p>{error}</p>
          <button className="btn-cancel" onClick={() => navigate('/users')}>
            Back to Users
          </button>
        </div>
      </div>
    );
  }

  // Show loading state
  if (fetching) {
    return (
      <div className="loading-container">
        <FaSpinner className="spinner-large" />
        <p>Loading user data...</p>
      </div>
    );
  }

  return (
    <div className="add-user-page">
      {/* Header with title on left, back button on right */}
      <div className="add-user-header">
        <div className="header-left">
          <div className="header-title">
            <h1>Edit User</h1>
          </div>
          <p className="header-subtitle">Update user account information and permissions</p>
        </div>
        <button className="back-button" onClick={() => navigate('/users')}>
          ← Back to Users
        </button>
      </div>

      <form onSubmit={handleSubmit} className="add-user-form-full">
        {/* Two Column Layout */}
        <div className="form-two-columns">

          {/* Left Column */}
          <div className="form-column">

            {/* Account Information */}
            <div className="form-section">
              <h3>
                <FaLock className="section-icon" />
                Account Information
              </h3>

              <div className="form-group">
                <label>Email: <span className="required">*</span></label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="user@example.com"
                  required
                />
              </div>

              <div className="form-group">
                <label>New Password (Optional):</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min. 8 characters)"
                />
                <p className="field-hint">Leave blank to keep current password</p>
              </div>

              <div className="form-group">
                <label>User Type: <span className="required">*</span></label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  className="form-select"
                  required
                >
                  {userTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                  {isLegacyType && (
                    <option value={formData.type} disabled>
                      {formData.type} (legacy)
                    </option>
                  )}
                </select>
                <p className="field-hint">
                  {formData.type === 'STAFF' && 'Staff have limited permissions based on assigned roles and city'}
                  {formData.type === 'CUSTOMER' && 'Customer has access to shop services and can make purchases'}
                  {isLegacyType && 'This user has a legacy type that can no longer be selected.'}
                </p>
              </div>
            </div>

            {/* Shop Selection */}
            <div className="form-section">
              <h3>
                <FaStore className="section-icon" />
                Shop Assignment
              </h3>

              <div className="form-group">
                <label>Select Shop: <span className="required">*</span></label>
                {loadingShops ? (
                  <div className="loading-select">
                    <FaSpinner className="spinner" /> Loading shops...
                  </div>
                ) : (
                  <select
                    value={selectedShop}
                    onChange={handleShopChange}
                    className="form-select"
                    required
                    disabled={!!shopIdFromPath}
                  >
                    <option value="">Select a shop</option>
                    {shops.map(shop => (
                      <option key={shop.shop_id} value={shop.shop_id}>
                        {shop.shop_name} ({shop.subdomain}.{shop.domain})
                      </option>
                    ))}
                  </select>
                )}
                {shopIdFromPath && currentShop && (
                  <p className="field-hint info-hint">
                    <FaStore /> Shop is fixed: {currentShop.shop_name}
                  </p>
                )}
              </div>
            </div>

            {/* City Selection */}
            <div className="form-section">
              <h3>
                <FaMapMarkerAlt className="section-icon" />
                City Selection
                {formData.type === 'STAFF' && <span className="required-badge">Required</span>}
                {formData.type === 'CUSTOMER' && <span className="info-hint"> (Optional)</span>}
              </h3>

              <div className="form-group">
                <label>
                  Select City:
                  {formData.type === 'STAFF' && <span className="required">*</span>}
                  {formData.type === 'CUSTOMER' && <span className="info-hint"> (Optional)</span>}
                </label>

                {loadingCities ? (
                  <div className="loading-select">
                    <FaSpinner className="spinner" /> Loading cities...
                  </div>
                ) : (
                  <select
                    name="selected_city"
                    value={formData.selected_city || ''}
                    onChange={handleInputChange}
                    className="form-select"
                    disabled={!selectedShop || cities.length === 0}
                  >
                    <option value="">-- Select a city --</option>
                    {cities.map((city, index) => (
                      <option key={index} value={city}>
                        {city}
                      </option>
                    ))}
                  </select>
                )}

                {!selectedShop && formData.type === 'STAFF' && (
                  <p className="field-hint warning-hint">
                    <FaStore /> Please select a shop first to see available cities
                  </p>
                )}

                {selectedShop && cities.length === 0 && !loadingCities && formData.type === 'STAFF' && (
                  <p className="field-hint warning-hint">
                    <FaMapMarkerAlt /> No cities available for this shop
                  </p>
                )}

                {selectedShop && cities.length > 0 && formData.type === 'STAFF' && (
                  <p className="field-hint success-hint">
                    <FaCheckCircle /> {cities.length} city(ies) available
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="form-column">

            {/* Basic Information */}
            <div className="form-section">
              <h3>
                <FaUserTag className="section-icon" />
                Basic Information
              </h3>

              <div className="form-row">
                <div className="form-group half">
                  <label>First Name: <span className="required">*</span></label>
                  <input
                    type="text"
                    name="first_Name"
                    value={formData.first_Name}
                    onChange={handleInputChange}
                    placeholder="Enter first name"
                    required
                  />
                </div>
                <div className="form-group half">
                  <label>Last Name: <span className="required">*</span></label>
                  <input
                    type="text"
                    name="last_Name"
                    value={formData.last_Name}
                    onChange={handleInputChange}
                    placeholder="Enter last name"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Phone Number:</label>
                <input
                  type="tel"
                  name="phone_Number"
                  value={formData.phone_Number}
                  onChange={handleInputChange}
                  placeholder="Enter Phone Number"
                />
              </div>

              {/* ✅ USER ADDRESS FORM */}
              <div className="form-group">
                <label>Address (Permanent):</label>

                <div className="user-address-form">
                  <div className="form-row">
                    <div className="form-group half">
                      <label>House No:</label>
                      <input
                        type="text"
                        name="house_no"
                        value={userAddress.house_no}
                        onChange={handleUserAddressChange}
                        placeholder="House / Building number"
                      />
                    </div>
                    <div className="form-group half">
                      <label>Street:</label>
                      <input
                        type="text"
                        name="street"
                        value={userAddress.street}
                        onChange={handleUserAddressChange}
                        placeholder="Street name"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group half">
                      <label>Area:</label>
                      <input
                        type="text"
                        name="area"
                        value={userAddress.area}
                        onChange={handleUserAddressChange}
                        placeholder="Area"
                      />
                    </div>
                    <div className="form-group half">
                      <label>Postcode:</label>
                      <input
                        type="text"
                        name="postcode"
                        value={userAddress.postcode}
                        onChange={handleUserAddressChange}
                        placeholder="Postal / ZIP code"
                      />
                    </div>
                  </div>

                  {buildFullAddress() && (
                    <div className="selected-address-preview">
                      <strong>Full Address Preview:</strong>
                      <p>{buildFullAddress()}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Company Information */}
            <div className="form-section">
              <h3>
                <FaBuilding className="section-icon" />
                Company Information
              </h3>

              <div className="form-group">
                <label>Company Name:</label>
                <input
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleInputChange}
                  placeholder="Company name"
                />
              </div>

              <div className="form-group">
                <label>Company Size:</label>
                <select
                  name="company_size"
                  value={formData.company_size}
                  onChange={handleInputChange}
                  className="form-select"
                >
                  {companySizes.map(size => (
                    <option key={size.value} value={size.value}>
                      {size.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>VAT Number:</label>
                <input
                  type="text"
                  name="Vat_number"
                  value={formData.Vat_number}
                  onChange={handleInputChange}
                  placeholder="VAT number"
                />
              </div>
            </div>

            {/* Status Options */}
            <div className="form-section">
              <h3>Status</h3>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                  />
                  <span className="checkbox-label">Active Account</span>
                </label>
                <p className="field-hint">Inactive users cannot login to the system</p>
              </div>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="form-actions-full">
          <button type="submit" className="btn-save" disabled={loading}>
            {loading ? <FaSpinner className="spinner" /> : <FaSave />}
            {loading ? 'UPDATING...' : 'UPDATE USER'}
          </button>
          <button type="button" className="btn-cancel" onClick={() => navigate('/users')}>
            Cancel
          </button>
        </div>
      </form>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Confirm Delete</h3>
              <button className="modal-close" onClick={() => setShowDeleteModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete user <strong>{formData.first_Name} {formData.last_Name}</strong>?</p>
              <p className="warning-text">This action cannot be undone. All data associated with this user will be permanently removed.</p>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </button>
              <button className="btn-delete" onClick={handleDeleteUser}>
                Delete User
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditUser;